import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { SingleSelect, FilterTrigger, getTriggerState } from '../../../FilterDropdown';
import { useFeature } from '../../../../hooks/useFeature';
import { useFeatureAnalytics } from '../../../../hooks/useFeatureAnalytics';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

/**
 * Feature-specific status filter. Fully controlled by the parent via
 * `selected`/`onSelect`; only the dropdown `open` state is local. Composes
 * SingleSelect + FilterTrigger directly (no SearchableSingleSelect layer).
 *
 * Status has a small fixed option set from feature config, so we don't
 * expose search or async fetch — options are mapped once from filterItems.
 */
const StatusFilter = ({ filterItems = [], selected, onSelect, label, showClear = true }) => {
  const { t } = useTranslation();
  const feature = useFeature();
  const config = feature?.config;
  const track = useFeatureAnalytics(config);
  const [open, setOpen] = useState(false);

  const options = useMemo(
    () =>
      filterItems
        .filter((item) => item.value && item.value !== 'all')
        .map((item) => ({
          value: item.value,
          label: item.label || capitalize(item.value)
        })),
    [filterItems]
  );

  const handleOnChange = useCallback(
    (option) => {
      // Track analytics when filter changes
      if (option && config?.analytics?.filterChange) {
        track('filterChange', { value: option.value });
      }

      onSelect?.(option ?? null);
      setOpen(false);
    },
    [onSelect, config, track]
  );

  const handleOnOpen = useCallback((isOpen) => {
    setOpen(isOpen);
  }, []);

  return (
    <SingleSelect
      open={open}
      onOpenChange={handleOnOpen}
      options={options}
      selectedOption={selected}
      onSelection={handleOnChange}>
      <FilterTrigger
        label={label || t('Status')}
        selectedOptions={selected ? [selected] : []}
        triggerState={getTriggerState(open)}
        onClear={showClear ? () => handleOnChange(null) : undefined}
      />
    </SingleSelect>
  );
};

StatusFilter.propTypes = {
  filterItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ),
  selected: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  }),
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string,
  showClear: PropTypes.bool
};

export default StatusFilter;
